import React, { useState } from 'react';
import useSWRImmutable from 'swr/immutable';

import { Container } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import { Translate } from '@hydra/common/components';
import ProjectShell from '@hydra/common/shell/ProjectShell';
import TableQueries from '@hydra/routes/keywords/components/TableQueries';
import GCSConnectPlaceholder from '@hydra/routes/keywords/components/GCSConnectPlaceholder';

import { getSearchAnalyticsWithPatterns } from '@hydra/utils/keywordsApi';

const useStyles = makeStyles(theme => ({
  container: {
    margin: `${theme.spacing(4)}px auto`
  },
  header: {
    marginBottom: theme.spacing(4)
  },
  heading: {
    margin: 0,
    marginBottom: theme.spacing(2),
    lineHeight: '32px',
    fontSize: 28
  }
}));

async function doFetch (url: string) {
  return getSearchAnalyticsWithPatterns({ url });
}

interface KeywordsContainerData {
  data: {
    url: string
    favicon?: string | null
    gsc: {
      gwtAccountEmail: null | string
      gwtSite: null | string
    }
  }
}

export default function KeywordsContainer ({
  data = {
    favicon: '',
    url: '',
    gsc: {
      gwtAccountEmail: null,
      gwtSite: null
    }
  }
}: KeywordsContainerData) {
  const theme = useTheme();
  const classes = useStyles();

  const { url, favicon } = data;

  const [gsc, setGsc] = useState(data.gsc);
  const gscSynced = gsc.gwtAccountEmail && gsc.gwtSite;

  const { data: fetchedData, isValidating, error } = useSWRImmutable(gscSynced ? url : null, doFetch, {
    dedupingInterval: 24 * 60 * 60 * 1000,
    errorRetryCount: 0
  });

  return (
    <ProjectShell section='keywords' favicon={favicon || null} bgColor={theme.palette.background.secondary}>
      <Container maxWidth='lg' className={classes.container}>
        <div className={classes.header}>
          <Translate
            content='gsc_keywords_title'
            className={classes.heading}
            component='h2'
          />
          <Translate content='gsc_keywords_subtitle' component='h5' />
        </div>
        {
          gscSynced
            ? <TableQueries data={fetchedData} url={url} isLoading={isValidating} hasError={!!error} />
            : <GCSConnectPlaceholder url={url} setGsc={setGsc} />
        }
      </Container>
    </ProjectShell>
  );
}
